import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'

import { primaryVisionStream, safeStorage, visionStreamOptions } from '@ctcells/core'
import { moduleLabel, t } from '@ctcells/i18n'

import { cx } from '../../atoms/cx.ts'
import { Icon } from '../../atoms/Icon.tsx'
import { Segmented } from '../../atoms/Segmented.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { VisionStream } from '../../figures/VisionStream.tsx'
import type { VisionStreamState } from '../../figures/visionStreamClient.ts'
import { FULL_VIEW_HOST_SELECTOR } from '../../shell/fullViewHost.ts'

import {
  usableAspect,
  VISION_ASPECT,
  VISION_FLOAT_LEGACY_KEYS,
  type VisionDevice,
} from './visionColumnModel.ts'

import './visionColumn.css'

/**
 * **장비 상세 카드의 비전 칸** — 고른 장비의 영상 한 칸(eric 2026-10-02 「비전쪽에.. 각각의 모듈 영상들... 장비의 상세..
 * (오른쪽) 내용 위에 넣어줘.. 그리고. 각각 비전 영상 넣고. 오른쪽에. .비전.. 은 없애..」).
 *
 * 장비별 보기 오른쪽 상세 카드(`EquipmentSheet` → `MonitoringPanel look="tiles"` 의 `lead`)에서 제목 바로 아래 ·
 * 메트릭 위에 선다 — 디자인팀 시안 `equipView()` 의 `.side` 안 `camCell` 자리 그대로다.
 * ⚠ 2026-09-28 ~ 10-02 에는 모니터링 오른쪽 열 「비전」 탭에 세 모듈 칸이 세로로 섰다(`VisionColumn`). 그 탭은 걷었다 —
 *   되살리지 마라. 한 화면에 영상은 고른 장비의 것 하나뿐이다.
 *
 * 🔴 **칸은 자료 유무와 무관하게 언제나 선다**(CLAUDE.md 「비전 탭은 항상 있다」의 취지 — 카메라는 장비의 구성 요소다).
 *    스트림 설정이 없으면 사유(`monitoring.noVisionFeed`)를 적고, 연결 실패 · 연결 중은 `VisionStream` 이 제 안에 적는다.
 * 🔴 **주소를 짓지 않는다** — `visionStreamFor(모듈)` 이다. 모듈별 지정(`VITE_VISION_STREAMS`)이 없으면 기본 스트림이 걸린다.
 * 🔴 **판정을 그리지 않는다.** 들어오는 것은 영상이지 판정이 아니다(BR-31) — fixture `visionFeeds` 를 읽지 않는다.
 * 🔴 영상 비율은 실물에서 받아 `--viscol-aspect` 로 넘긴다(전에는 16:9). 해상도 숫자를 박지 않는다.
 * ⚠ 장비가 바뀌면 부르는 쪽이 `key` 로 칸을 새로 세운다 — 다른 모듈의 연결을 물려받지 않는다.
 */
export function VisionPane({
  device,
  fullscreen = false,
  bare = false,
}: {
  device: VisionDevice
  /**
   * 영상 오른쪽 아래에 전체 화면 단추를 겹쳐 둔다(모듈 앱 · 통합 콘솔 공통, eric 2026-10-27).
   * 확대·축소는 같은 단추를 갱신해 키보드 포커스를 유지한다.
   */
  fullscreen?: boolean | undefined
  /**
   * 머리 줄의 장비 이름 · 길(WebRTC · MSE) · 해상도 글자를 뺀다 — **모듈 앱만**(eric 2026-10-18 「원심분리기의 텍스트 없애고.. WebRTC 텍스트도
   * 없애줘」 · 화면 전체가 그 장비 하나라 이름이 겹친다). 이름은 스크린리더용으로 남는다
   */
  bare?: boolean | undefined
}) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [aspect, setAspect] = useState(VISION_ASPECT)

  /* 떠 있는 창이 남긴 저장값을 치운다 — 읽지 않는다 */
  useEffect(() => {
    for (const key of VISION_FLOAT_LEGACY_KEYS) safeStorage.remove(key)
  }, [])

  /* 🔴 **영상 비율을 실물에서 받는다** — `loadedmetadata` · `resize` 는 거품이 안 올라와 «잡기 단계»로 듣는다 */
  useEffect(() => {
    const root = rootRef.current
    if (root === null) return
    const onMeta = (event: Event) => {
      const video = event.target
      if (!(video instanceof HTMLVideoElement)) return
      const seen = usableAspect(video.videoWidth, video.videoHeight)
      if (seen === undefined) return
      setAspect((prev) => (Math.abs(prev - seen) <= 1e-3 ? prev : seen))
    }
    root.addEventListener('loadedmetadata', onMeta, true)
    root.addEventListener('resize', onMeta, true)
    return () => {
      root.removeEventListener('loadedmetadata', onMeta, true)
      root.removeEventListener('resize', onMeta, true)
    }
  }, [])

  const style = { '--viscol-aspect': String(aspect) } as CSSProperties

  return (
    <div className="viscol" ref={rootRef} style={style}>
      <VisionCell bare={bare} device={device} fullscreen={fullscreen} />
    </div>
  )
}

/** 연결 상태 → 배지(원값은 `statusKind` 사전의 낱말을 빌린다 — 종류만 고른다. 글자는 `label`) */
function streamBadge(state: VisionStreamState | undefined): { status: string; label: string } {
  if (state === undefined) return { status: 'IDLE', label: t('vision.badge.none') }
  if (state.phase === 'playing') return { status: 'running', label: t('monitoring.live') }
  if (state.phase === 'connecting') return { status: 'IDLE', label: t('vision.badge.connecting') }
  return state.reason === 'unsupported'
    ? { status: 'IDLE', label: t('vision.badge.unsupported') }
    : { status: 'WARN', label: t('vision.badge.failed') }
}

/**
 * **영상 칸 하나** — 머리 줄(왼쪽 위 장비 이름 · 재생 중이면 길 · 해상도 · 오른쪽 연결 상태 배지) + 아래 영상
 * (디자인팀 시안 `camCell` — 이름은 왼쪽 위 제목 + 연결 상태 배지).
 *
 * 🔴 **배지는 `VisionStream` 이 아는 사실 그대로다**(`onState`) — 연결 중 · LIVE · 연결 실패 · 재생 불가.
 *    스트림 설정이 없으면 「연결 없음」이고 영상 자리에는 종전 문구(`monitoring.noVisionFeed`)가 그대로 선다
 *    (연결 전의 밝은 표시는 운영 방식 유지 — 시안도 그쪽을 권한다).
 * 🔴 영상 위 정보 글자(`● LIVE …`)는 이 칸에서 끈다(`meta={false}`) — 머리 줄이 같은 사실을 이미 말한다.
 * 🔴 **판정을 그리지 않는다**(BR-31). 칸은 자료 유무와 무관하게 선다.
 */
function VisionCell({
  device,
  fullscreen,
  bare,
}: {
  device: VisionDevice
  fullscreen: boolean
  bare: boolean
}) {
  const options = visionStreamOptions(device)
  const [picked, pick] = usePickedStream(
    device,
    options.map((option) => option.stream),
    primaryVisionStream(options),
  )
  const stream = picked
  const name = moduleLabel(device)
  const [state, setState] = useState<VisionStreamState | undefined>(undefined)
  const { viewRef, full, enter, exit } = useFullView()
  /* 카메라가 둘 이상이면 고르는 탭(IR · Color — eric 2026-10-18). 하나면 탭이 없다 */
  const tabs =
    options.length > 1 && stream !== undefined ? (
      <span className="viscol__tabs">
        <Segmented
          label={t('vision.camera.pick', { device: name })}
          onChange={(next) => {
            setState(undefined)
            pick(next)
          }}
          /* 탭 이름은 설정(`VITE_VISION_STREAMS` 의 `이름:스트림`)이 정한다 — 화면이 바꿔 적지 않는다 */
          options={options.map((option) => ({ value: option.stream, label: option.label }))}
          value={stream}
        />
      </span>
    ) : null
  return (
    <figure className="viscol__cell" data-device={device}>
      <VisionBar
        as="figcaption"
        configured={stream !== undefined}
        stacked={false}
        tech={!bare}
        label={
          bare ? (
            <>
              {/* 모듈 앱 — 눈에는 「비전」(eric 2026-10-18 「앞에.. 비전 이라고 텍스트 붙여줘」), 스크린리더에는 장비 이름 */}
              <span aria-hidden="true" className="viscol__label viscol__label--title">
                {t('figTab.vision')}
              </span>
              <span className="a-sr">{name}</span>
            </>
          ) : (
            <>
              <span className="viscol__label">{name}</span>
            </>
          )
        }
        /* 한 줄 머리: 제목 … [연결] [카메라 탭] — 연결이 탭보다 앞이다(eric 2026-10-26) */
        lead={tabs}
        state={state}
      />
      <div className={cx('viscol__view', full && 'viscol__view--full')} ref={viewRef}>
        {stream === undefined ? (
          /* 🔴 **설정**의 문제다 — 「카메라가 못 봤다」가 아니다. 문구를 줄이지 마라 */
          <p className="viscol__empty">{t('monitoring.noVisionFeed')}</p>
        ) : (
          <VisionStream
            /* 카메라를 바꾸면 연결을 새로 세운다 — 앞 카메라의 연결 · 상태를 물려받지 않는다 */
            key={stream}
            label={t('monitoring.flow.vision.head', { device: name })}
            meta={false}
            moduleKey={device}
            onState={setState}
            stream={stream}
          />
        )}
        {fullscreen || full ? (
          <button
            aria-label={
              full ? t('vision.fullscreen.exit') : t('vision.fullscreen.enter', { device: name })
            }
            className={full ? 'viscol__unfull' : 'viscol__full'}
            onClick={full ? exit : enter}
            title={
              full ? t('vision.fullscreen.exit') : t('vision.fullscreen.enter', { device: name })
            }
            type="button"
          >
            {full ? <Icon name="collapse" size="lg" /> : <Icon name="expand" size="md" />}
          </button>
        ) : null}
      </div>
    </figure>
  )
}

/**
 * 고른 카메라 — 이 장비에서 마지막으로 고른 것을 기억한다(브라우저 저장 · 없거나 설정에서 빠졌으면 설정의 기본 카메라).
 * 저장이 막힌 환경에서도 이번 화면 동안은 고른 것이 선다.
 */
function usePickedStream(
  device: VisionDevice,
  streams: readonly string[],
  primary: string | undefined,
): [string | undefined, (next: string) => void] {
  const storageKey = `ctcells:vision:camera:${device}`
  const [picked, setPicked] = useState<string | null>(() => safeStorage.read(storageKey))
  const current = picked !== null && streams.includes(picked) ? picked : primary
  const pick = (next: string) => {
    setPicked(next)
    safeStorage.write(storageKey, next)
  }
  return [current, pick]
}

/**
 * **영상 자리 확대** — 셸이 표지(`fullViewHost.ts`)를 단 본문이 있으면 **그 칸 크기로만** 덮는다(머리줄은 남는다 ·
 * eric 2026-10-26 「상위의 헤더는 덮지 말고.. 그 아래만 하게 해줘」). 통합 콘솔 · 모듈 앱이 같은 길이다.
 * 표지가 없는 셸(장비 관점 · 태블릿 · 패드)만 종전대로 브라우저 전체 화면(Fullscreen API)을 청하고, 못 받으면
 * 창 안을 덮는 겹침(`.viscol__view--full`)으로 선다.
 * 끝내기: 영상 위 단추 · Esc(브라우저 전체 화면이면 브라우저가 풀고 `fullscreenchange` 로 따라 닫는다).
 * 🔴 영상 연결은 그대로다 — 같은 `<video>` 를 키울 뿐 DOM 을 옮기거나 다시 붙지 않는다.
 * 🔴 자리 맞추기: 본문 크기 변화(ResizeObserver) · 창 크기 · 스크롤(캡처)에 다시 잰다. 닫으면 인라인 스타일까지 걷는다.
 */
function useFullView() {
  const viewRef = useRef<HTMLDivElement | null>(null)
  const [full, setFull] = useState(false)
  const hostRef = useRef<HTMLElement | null>(null)

  useLayoutEffect(() => {
    const host = hostRef.current
    const view = viewRef.current
    if (!full || host === null || view === null) return
    const fit = () => {
      const rect = host.getBoundingClientRect()
      view.style.inset = 'auto'
      view.style.top = `${String(rect.top)}px`
      view.style.left = `${String(rect.left)}px`
      view.style.width = `${String(rect.width)}px`
      view.style.height = `${String(rect.height)}px`
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(host)
    window.addEventListener('resize', fit)
    window.addEventListener('scroll', fit, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
      window.removeEventListener('scroll', fit, true)
      for (const property of ['inset', 'top', 'left', 'width', 'height'])
        view.style.removeProperty(property)
    }
  }, [full])

  useEffect(() => {
    if (!full) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFull(false)
    }
    const onChange = () => {
      if (hostRef.current === null && document.fullscreenElement == null) setFull(false)
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('fullscreenchange', onChange)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('fullscreenchange', onChange)
    }
  }, [full])

  const enter = () => {
    const view = viewRef.current
    hostRef.current = view?.closest<HTMLElement>(FULL_VIEW_HOST_SELECTOR) ?? null
    setFull(true)
    if (hostRef.current !== null) return
    if (view !== null && typeof view.requestFullscreen === 'function') {
      view.requestFullscreen().catch(() => undefined)
    }
  }
  const exit = () => {
    setFull(false)
    if (
      hostRef.current === null &&
      document.fullscreenElement != null &&
      typeof document.exitFullscreen === 'function'
    ) {
      document.exitFullscreen().catch(() => undefined)
    }
  }
  return { viewRef, full, enter, exit }
}

/**
 * **영상 칸 머리 줄** — 왼쪽 이름 · 재생 중이면 길(WebRTC · MSE)과 해상도 · 오른쪽 연결 상태 배지.
 *
 * 장비 상세 카드의 비전 칸(`VisionPane`)이 쓴다 — 다른 영상 자리가 생기면 이 줄을 같이 쓴다(두 벌로 만들지 않는다).
 * 🔴 배지 · 길 · 해상도는 `VisionStream` 이 알린 사실(`state`) 그대로다. `configured` 가 거짓이면(스트림 설정 없음)
 *    「연결 없음」이고, 참인데 아직 알림 전이면 「연결 중」이다.
 * 🔴 `badge={false}` 는 **영상이 아니라 fixture 그림이 서는 자리**(demo `visionFeed`)에서만 — 그 그림이 제 안에
 *    LIVE 표지를 따로 가져서 배지가 같은 사실을 다르게 말하게 된다.
 */
export function VisionBar({
  as: Tag = 'div',
  label,
  configured,
  state,
  badge: showBadge = true,
  tech: showTech = true,
  stacked = false,
  lead,
  action,
}: {
  as?: 'div' | 'figcaption'
  /** 이름 자리 — 부르는 쪽이 원소(제목 수준 등)를 정한다 */
  label: ReactNode
  configured: boolean
  state: VisionStreamState | undefined
  badge?: boolean | undefined
  /** 두 줄 — 윗줄 오른쪽에 카메라 탭, 아랫줄 오른쪽에 연결 배지 · 조작(모듈 앱 · eric 2026-10-18 「IR 과 컬러탭을 뒷 정렬로 하고. 다음 줄에. 연결과 전체 화면 아이콘」) */
  stacked?: boolean | undefined
  /** 두 줄 머리 아랫줄 왼쪽에 서는 것(카메라 탭) */
  lead?: ReactNode
  /** 길 · 해상도 글자를 적는가(모듈 앱은 끈다 — `VisionPane bare`) */
  tech?: boolean | undefined
  /** 배지 오른쪽 끝에 붙는 조작(전체 화면 단추) */
  action?: ReactNode
}) {
  const badge = streamBadge(configured ? (state ?? CONNECTING) : undefined)
  const tech =
    showTech && configured && state?.phase === 'playing'
      ? [
          state.mode === undefined ? undefined : state.mode === 'webrtc' ? 'WebRTC' : 'MSE',
          state.size === undefined
            ? undefined
            : `${String(state.size.width)}×${String(state.size.height)}`,
        ]
          .filter((part) => part !== undefined)
          .join(' · ')
      : ''
  const badgeNode = showBadge ? (
    <span className="viscol__badge">
      {/* 테 없는 알약 — 연결 · 연결 중 · 실패 · 연결 없음 모두(eric 2026-10-29 「연결도 테두리 없애주고」) · 색은 그대로 */}
      <StatusBadge flat label={badge.label} status={badge.status} />
    </span>
  ) : null
  return (
    <Tag className={cx('viscol__bar', stacked && 'viscol__bar--stack')}>
      {label}
      {tech === '' ? null : <span className="viscol__tech">{tech}</span>}
      {stacked ? (
        /* 두 줄 머리 — 자리는 CSS 격자가 정한다(윗줄 제목 · 조작, 아랫줄 카메라 탭 · 연결 배지) */
        <>
          {action}
          {lead}
          {badgeNode}
        </>
      ) : (
        /* 한 줄 머리 — 제목 … [연결] [카메라 탭](eric 2026-10-26 「연결 칩과 Color/IR 탭 순서 바꿔줘」) */
        <>
          {badgeNode}
          {lead}
          {action}
        </>
      )}
    </Tag>
  )
}

/** 첫 알림이 오기 전 — `VisionStream` 의 첫 상태와 같다 */
const CONNECTING: VisionStreamState = {
  phase: 'connecting',
  mode: undefined,
  reason: undefined,
  size: undefined,
}
